import { ModalAction } from '@overlayqa/shared/ui';
/**
 * Design-token sources for a project: upload a tokens file, then choose which
 * sources feed the Design System Audit.
 *
 * THE COPY IS THE FEATURE HERE, and it is the product of a psychology teardown
 * of an earlier draft. Two rules it holds deliberately:
 *
 * 1. It does NOT lead with "requires a Figma Enterprise plan". That made the
 *    user diagnose their own Figma tier before they could act, and it read as
 *    us gatekeeping when the gate belongs to Figma. Serial position: the first
 *    words carry the meaning, so they are the action and its outcome.
 * 2. It carries "Works with any Figma plan", which is TRUE (an upload is
 *    plan-agnostic) and turns someone else's paywall from our apology into our
 *    advantage.
 *
 * The Figma export route is an always-open tip below the upload (Emily,
 * 2026-09-17: "default expanded ... make it look like a tip UI"; it was a
 * <details> disclosure). It says "plugin" on purpose: native variable export is
 * not available to every plan, so copy implying a built-in export button would
 * send a Professional-plan user hunting for a control that does not exist.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { BulbIcon } from '@hugeicons/core-free-icons';
import { TOKEN_UPLOAD_ERRORS, tokenUploadFailureReason, toUserFacingMessage } from '@overlayqa/shared';
import { useAuth } from '../../lib/auth';
import { tokenSourcesApi, type TokenSource } from '../../lib/api/tokenSources';
// Imported RELATIVELY, not as `@/lib/analytics`, and that is load-bearing
// rather than a style choice. jest's moduleNameMapper redirects the relative
// specifier to a stub for every suite (jest.config.js maps one, two and three
// levels), while the `@/` alias resolves to the REAL module, which reads
// `import.meta.env` at load and cannot parse under ts-jest. Eleven existing
// suites render this component transitively; the alias form broke all of them.
import { capture } from '../../lib/analytics';
import { LoadError } from '../shared/IssuesLoadError';
import { log } from '@/lib/logger';

// One definition across server route, dashboard, and extension dropzone.
const NOT_JSON = TOKEN_UPLOAD_ERRORS.notJson;

interface DesignTokenSourcesProps {
  projectId: string;
}

/**
 * Report a rejected upload.
 *
 * INCIDENT (2026-08-14). user-9d2185@example.com hit the not-JSON rejection here
 * and it produced NOTHING in analytics. The only trace was three
 * `$dead_click`s on "Upload tokens file", which is the weakest possible signal
 * and sits in the documented false-positive class. It was found by watching a
 * session recording. The design-token audit is the wedge, so a silent
 * rejection on its main ingestion path is the wrong thing to be blind to.
 *
 * WHY THE CLIENT OWNS THIS. handleFile catches an unparseable file in the
 * BROWSER, so a server-side emitter is structurally blind to the exact failure
 * that prompted the work. The dashboard is also the only client of the upload
 * route (the extension's own token dropzone is session-scoped and never
 * uploads), so reporting here covers every rejection the server can see AND
 * the ones it cannot.
 *
 * WHAT IT DELIBERATELY DOES NOT CARRY: the file name and the file body. A name
 * can carry a client's name and the body can be an unreleased design system;
 * neither belongs on a distinct_id. The reason KEY is the diagnostic, and it
 * is all that is needed to tell "our parser is too strict" apart from "they
 * uploaded a CSV".
 */
function reportFailureFor(projectId: string, message: string): void {
  capture('token_upload_failed', {
    project_id: projectId,
    reason: tokenUploadFailureReason(message),
  });
}

export function DesignTokenSources({ projectId }: DesignTokenSourcesProps) {
  const { getToken } = useAuth();
  const [sources, setSources] = useState<TokenSource[]>([]);
  const [listError, setListError] = useState<Error | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  // Only the newest refresh owns the UI. Two can be in flight at once (the
  // upload path awaits its own refresh, and Retry is clickable while one is
  // running), and once a failure is VISIBLE the loser rejecting last would
  // paint an error card over the list the winner just fetched. Guards the
  // sources write too, which had the same latent race with nothing to show
  // for it while failures were silent.
  const refreshEpoch = useRef(0);

  const refresh = useCallback(async () => {
    const epoch = ++refreshEpoch.current;
    try {
      const next = await tokenSourcesApi.list(await getToken(), projectId);
      if (epoch !== refreshEpoch.current) return;
      setSources(next);
      // Clear on recovery, or a retry that worked leaves its own error card
      // sitting above the list it just fetched.
      setListError(null);
    } catch (err) {
      // A silent catch here is what let the 2026-08-11 401 (every call sent
      // with no Clerk token) sit invisible: `sources` stayed [] and the section
      // rendered exactly like a project that has none. Empty and failed must
      // stay tellable apart, or a user re-uploads and duplicates a source.
      // Logged even when superseded — a stale failure is still worth a line —
      // but only the current request may write to the screen.
      log.warn('[DesignTokenSources] list failed', { err });
      if (epoch !== refreshEpoch.current) return;
      setListError(err instanceof Error ? err : new Error(String(err)));
    }
  }, [projectId, getToken]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleFile = async (file: File) => {
    setError(null);
    setSuccess(null);
    setBusy(true);

    try {
      const text = await file.text();

      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        // Caught in the browser so an unreadable file never costs a round trip.
        // Which is exactly why the outcome is reported client-side and not on
        // the server: this rejection never reaches it. See reportFailureFor.
        setError(NOT_JSON);
        reportFailureFor(projectId, NOT_JSON);
        return;
      }

      const created = await tokenSourcesApi.upload(await getToken(), projectId, file.name, parsed);
      setSuccess(
        `${created.tokenCount} tokens added. Your next Design System Audit will compare against them.`,
      );
      // The denominator. A failure count with no successes beside it cannot
      // answer "what fraction of uploads are rejected", which is the only
      // question the failure event is worth firing for.
      capture('token_upload_completed', {
        project_id: projectId,
        token_count: created.tokenCount,
      });
      await refresh();
    } catch (err) {
      // The server's message is already blameless and actionable. Paraphrasing
      // it here would fork the wording and drop the "what to do next" half.
      const message = toUserFacingMessage(err, 'That file could not be read.');
      setError(message);
      reportFailureFor(projectId, message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const toggle = async (source: TokenSource) => {
    const next = !source.enabled;
    setSources((prev) => prev.map((s) => (s.id === source.id ? { ...s, enabled: next } : s)));

    try {
      await tokenSourcesApi.setEnabled(await getToken(), projectId, source.id, next);
    } catch (err) {
      log.warn('[DesignTokenSources] toggle failed', { err });
      // Put the checkbox back rather than leaving the UI claiming a selection
      // the server never accepted.
      setSources((prev) => prev.map((s) => (s.id === source.id ? { ...s, enabled: !next } : s)));
      setError('That change did not save. Please try again.');
    }
  };

  return (
    <section>
      <p className="field-hint mb-2">
        Upload your tokens file to check the live site against your design system.
      </p>
      <p className="field-hint mb-3">Works with any Figma plan.</p>

      {/* A failed load says so, with a Retry. The shared LoadError rather than a
          bespoke banner: eight other sections already use it, so the wording,
          the status-aware explanation and recovery actions come from one
          place. A project with genuinely no sources renders NOTHING here and
          keeps only the upload prompt, which is what keeps the two states
          distinguishable. */}
      {listError && (
        <div className="mb-3">
          <LoadError
            compact
            resource="design token sources"
            error={listError}
            onRetry={() => refresh()}
          />
        </div>
      )}

      {sources.length > 0 && (
        <ul className="mb-3 divide-y divide-gray-100 rounded-lg border border-gray-200">
          {sources.map((source) => (
            <li key={source.id} className="flex items-center gap-3 px-3 py-2">
              <input
                type="checkbox"
                id={`token-source-${source.id}`}
                checked={source.enabled}
                onChange={() => void toggle(source)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600"
              />
              <label htmlFor={`token-source-${source.id}`} className="flex-1 text-sm">
                {source.label}
              </label>
              <span className="text-xs text-gray-500">{source.tokenCount} tokens</span>
            </li>
          ))}
        </ul>
      )}

      <input
        ref={fileRef}
        data-testid="token-file-input"
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
        }}
      />

      <button
        type="button"
        className="btn btn-secondary btn-sm"
        disabled={busy}
        onClick={() => fileRef.current?.click()}
      ><ModalAction kind="upload">
        {busy ? 'Uploading...' : 'Upload tokens file'}
      </ModalAction></button>

      {error && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {error}
        </p>
      )}
      {/* Exists before the upload, so the result is announced (2026-10-25 audit, finding 1). */}
      <div role="status">{success && <p className="mt-2 text-xs text-green-700">{success}</p>}</div>

      {/* The suggestion callout FigmaUrlInput already uses (bg-primary-50,
          border-primary-200, BulbIcon in primary-500), so the dashboard keeps
          one tip style rather than growing a second. */}
      <div role="note" className="mt-3 p-3 bg-primary-50 border border-primary-200 rounded text-primary-700 text-xs">
        <div className="flex items-start gap-2">
          <HugeiconsIcon icon={BulbIcon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4 flex-shrink-0 text-primary-500" aria-hidden="true" />
          <p className="flex-1">
            <strong>Exporting from Figma?</strong> Export your variables with a plugin such as Tokens Studio, then upload the file here. We
            read the W3C design tokens format, which Tokens Studio and Style Dictionary both export.
          </p>
        </div>
      </div>
    </section>
  );
}
